import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Divider,
  Button,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as BackIcon,
  Dashboard as OverviewIcon,
  People as PersonnelIcon,
  Badge as FicheIcon,
  AccessTime as PointageIcon,
  HowToReg as DeviceUsersIcon,
  Description as DocumentIcon,
  WorkOutline as AttestTravailIcon,
  AttachMoney as AttestSalaireIcon,
  CreditCard as PretIcon,
  MoneyOff as AcompteIcon,
  EventBusy as DemandeCongeIcon,
  ExitToApp as AbsenceIcon,
  AccountBalance as PaieIcon,
  Calculate as CalculIcon,
  Receipt as STCIcon,
  Settings as ConfigIcon,
  Assessment as SyntheseIcon,
  CalendarMonth as Etat9421Icon,
  MenuBook as JournalIcon,
  LibraryBooks as LivrePaieIcon,
  ReceiptLong as BulletinIcon,
  SwapHoriz as VirementIcon,
  AccountBalanceWallet as CNSSIcon,
  Event as JoursFeriesIcon,
  CardGiftcard as PrimeIcon,
  Percent as CotisationIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import ThemeToggle from '../../components/ThemeToggle';
import NotificationsBell from '../../components/NotificationsBell';
import HeaderChatButton from '../../components/HeaderChatButton';

const drawerWidth = 300;
const collapsedDrawerWidth = 64;

// HR Module color
const HR_COLOR = '#00897B';
const HR_COLOR_LIGHT = '#4DB6AC';
const HR_COLOR_HOVER = '#00695C';

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  exact?: boolean;
  disabled?: boolean;
  miniModuleName?: string;
  children?: MenuItem[];
}

const HRLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    personnel: true,
    documentaire: false,
    paie: false,
    synthese: false,
    configuration: false,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleBackToMain = () => {
    navigate('/dashboard');
  };

  const handleSectionToggle = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Get current page title based on route
  const getPageTitle = (): string => {
    const path = location.pathname;
    const titleMap: Record<string, string> = {
      '/hr': 'Tableau de bord RH',
      '/hr/personnel/fiches': 'Fiche du Personnel',
      '/hr/personnel/pointage': 'Pointage du Personnel',
      '/hr/personnel/pointeuse': 'Pointage Biométrique',
      '/hr/personnel/utilisateurs-pointeuse': 'Configuration Pointeuse',
      '/hr/documents/attestation-travail': 'Attestation Travail',
      '/hr/documents/attestation-salaire': 'Attestation Salaire',
      '/hr/documents/demande-pret': 'Demande Prêt',
      '/hr/documents/demande-acompte': 'Demande Acompte',
      '/hr/documents/demande-conge': 'Demande Congé',
      '/hr/documents/autorisation-absence': 'Autorisation Absence',
      '/hr/paie/base-calcul': 'Base de Calcul Paie',
      '/hr/paie/stc': 'Gestion STC',
      '/hr/paie/configuration': 'Configuration Paie',
      '/hr/synthese/etat-9421': 'État 9421',
      '/hr/synthese/journal-controle': 'Journal Contrôle Comptable',
      '/hr/synthese/livre-paie': 'Livre de Paie',
      '/hr/configuration/jours-feries': 'Jours Fériés',
      '/hr/configuration/primes-exonerees': 'Primes Exonérées',
      '/hr/configuration/primes-non-exonerees': 'Primes Non Exonérées',
      '/hr/configuration/cotisations-sociales': 'Cotisations Sociales',
    };
    return titleMap[path] || 'Ressources Humaines';
  };

  const hrMenuItems: MenuItem[] = [
    {
      text: 'Tableau de bord',
      icon: <OverviewIcon />,
      path: '/hr',
      exact: true,
    },
    {
      text: 'Gestion du Personnel',
      icon: <PersonnelIcon />,
      children: [
        {
          text: 'Fiche du Personnel',
          icon: <FicheIcon />,
          path: '/hr/personnel/fiches',
          miniModuleName: 'Employés',
          disabled: false,
        },
        {
          text: 'Pointage',
          icon: <PointageIcon />,
          path: '/hr/personnel/pointage',
          miniModuleName: 'Pointage du Personnel',
          disabled: false,
        },
        {
          text: 'Pointage Biométrique',
          icon: <PointageIcon />,
          path: '/hr/personnel/pointeuse',
          miniModuleName: 'Pointage Biométrique',
          disabled: false,
        },
      ],
    },
    {
      text: 'Documentaire',
      icon: <DocumentIcon />,
      children: [
        {
          text: 'Attestation Travail',
          icon: <AttestTravailIcon />,
          path: '/hr/documents/attestation-travail',
          miniModuleName: 'Attestations Travail',
          disabled: false,
        },
        {
          text: 'Attestation Salaire',
          icon: <AttestSalaireIcon />,
          path: '/hr/documents/attestation-salaire',
          miniModuleName: 'Attestations Salaire',
          disabled: false,
        },
        {
          text: 'Demande Prêt',
          icon: <PretIcon />,
          path: '/hr/documents/demande-pret',
          miniModuleName: 'Prêts',
          disabled: false,
        },
        {
          text: 'Demande Acompte',
          icon: <AcompteIcon />,
          path: '/hr/documents/demande-acompte',
          miniModuleName: 'Acomptes',
          disabled: false,
        },
        {
          text: 'Demande Congé',
          icon: <DemandeCongeIcon />,
          path: '/hr/documents/demande-conge',
          miniModuleName: 'Congés',
          disabled: false,
        },
        {
          text: 'Autorisation Absence',
          icon: <AbsenceIcon />,
          path: '/hr/documents/autorisation-absence',
          miniModuleName: 'Autorisations Absence',
          disabled: false,
        },
      ],
    },
    {
      text: 'Gestion Paie',
      icon: <PaieIcon />,
      children: [
        {
          text: 'Base de Calcul Paie',
          icon: <CalculIcon />,
          path: '/hr/paie/base-calcul',
          miniModuleName: 'Base de Calcul Paie',
          disabled: false,
        },
        {
          text: 'Gestion STC',
          icon: <STCIcon />,
          path: '/hr/paie/stc',
          miniModuleName: 'Employés',
          disabled: false,
        },
        {
          text: 'Configuration',
          icon: <ConfigIcon />,
          path: '/hr/paie/configuration',
          disabled: true,
        },
      ],
    },
    {
      text: 'Synthèse',
      icon: <SyntheseIcon />,
      children: [
        {
          text: 'État 9421 (Annuel)',
          icon: <Etat9421Icon />,
          path: '/hr/synthese/etat-9421',
          disabled: true,
        },
        {
          text: 'Journal Contrôle Comptable',
          icon: <JournalIcon />,
          path: '/hr/synthese/journal-controle',
          disabled: true,
        },
        {
          text: 'Livre de Paie',
          icon: <LivrePaieIcon />,
          path: '/hr/synthese/livre-paie',
          disabled: true,
        },
      ],
    },
    {
      text: 'Configuration',
      icon: <ConfigIcon />,
      children: [
        {
          text: 'Configuration Pointeuse',
          icon: <DeviceUsersIcon />,
          path: '/hr/personnel/utilisateurs-pointeuse',
          miniModuleName: 'Utilisateurs Pointeuse',
          disabled: false,
        },
        {
          text: 'Jours Fériés',
          icon: <JoursFeriesIcon />,
          path: '/hr/configuration/jours-feries',
          miniModuleName: 'Jours Fériés',
          disabled: false,
        },
        {
          text: 'Primes Exonérées',
          icon: <PrimeIcon />,
          path: '/hr/configuration/primes-exonerees',
          miniModuleName: 'Primes Exonérées',
          disabled: false,
        },
        {
          text: 'Primes Non Exonérées',
          icon: <PrimeIcon />,
          path: '/hr/configuration/primes-non-exonerees',
          miniModuleName: 'Primes Non Exonérées',
          disabled: false,
        },
        {
          text: 'Cotisations Sociales',
          icon: <CotisationIcon />,
          path: '/hr/configuration/cotisations-sociales',
          miniModuleName: 'Cotisations Sociales',
          disabled: false,
        },
      ],
    },
  ];

  const isActiveRoute = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getSectionKey = (text: string): string => {
    const keyMap: Record<string, string> = {
      'Gestion du Personnel': 'personnel',
      'Documentaire': 'documentaire',
      'Gestion Paie': 'paie',
      'Synthèse': 'synthese',
      'Configuration': 'configuration',
    };
    return keyMap[text] || text.toLowerCase();
  };

  const renderMenuItem = (item: MenuItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const sectionKey = getSectionKey(item.text);
    const isOpen = openSections[sectionKey];

    // Check permission for mini module
    const hasAccess = item.miniModuleName
      ? hasPermission('HR', 'view', item.miniModuleName)
      : true;
    const isDisabled = item.disabled || !hasAccess;
    const isActive = Boolean(item.path && !isDisabled && isActiveRoute(item.path, item.exact));

    if (hasChildren) {
      return (
        <React.Fragment key={item.text}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleSectionToggle(sectionKey)}
              sx={{
                minHeight: 48,
                justifyContent: desktopOpen ? 'initial' : 'center',
                px: desktopOpen ? 2.5 : 0,
                backgroundColor: isOpen ? 'rgba(0, 137, 123, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 137, 123, 0.12)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: desktopOpen ? 2 : 0,
                  justifyContent: 'center',
                  color: isOpen ? HR_COLOR : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {desktopOpen && (
                <>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isOpen ? 600 : 500,
                      fontSize: '0.9rem',
                      color: isOpen ? HR_COLOR : 'inherit',
                    }}
                  />
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>
          </ListItem>
          {desktopOpen && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.children!.map((child) => renderMenuItem(child, level + 1))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    }

    const tooltipTitle = !hasAccess
      ? 'Accès non autorisé'
      : item.disabled
      ? 'Fonctionnalité en cours de développement'
      : '';

    return (
      <ListItem key={item.text} disablePadding>
        <Tooltip title={tooltipTitle} placement="right" arrow>
          <span style={{ width: '100%' }}>
            <ListItemButton
              selected={isActive}
              onClick={!isDisabled && item.path ? () => navigate(item.path!) : undefined}
              disabled={isDisabled}
              sx={{
                minHeight: 44,
                justifyContent: desktopOpen ? 'initial' : 'center',
                px: desktopOpen ? 2.5 : 0,
                pl: desktopOpen ? 2.5 + level * 2 : 0,
                '&.Mui-selected': {
                  backgroundColor: HR_COLOR_LIGHT,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: HR_COLOR,
                  },
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                },
                cursor: !isDisabled ? 'pointer' : 'not-allowed',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: desktopOpen ? 2 : 0,
                  justifyContent: 'center',
                  color: isActive ? 'white' : !isDisabled ? 'inherit' : 'text.disabled',
                  '& .MuiSvgIcon-root': {
                    fontSize: level > 0 ? '1.2rem' : '1.5rem',
                  },
                }}
              >
                {item.icon}
              </ListItemIcon>
              {desktopOpen && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                    fontSize: level > 0 ? '0.85rem' : '0.9rem',
                    color: !isDisabled ? 'inherit' : 'text.disabled',
                  }}
                />
              )}
            </ListItemButton>
          </span>
        </Tooltip>
      </ListItem>
    );
  };

  const drawer = (
    <div>
      <Toolbar>
        {desktopOpen ? (
          <Button
            fullWidth
            startIcon={<BackIcon />}
            onClick={handleBackToMain}
            variant="outlined"
            size="small"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              justifyContent: 'flex-start',
              borderColor: HR_COLOR,
              color: HR_COLOR,
              '&:hover': {
                borderColor: HR_COLOR_HOVER,
                backgroundColor: 'rgba(0, 137, 123, 0.08)',
              },
            }}
          >
            Retour au Menu Principal
          </Button>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <IconButton onClick={handleBackToMain} size="small" sx={{ color: HR_COLOR }}>
              <BackIcon />
            </IconButton>
          </Box>
        )}
      </Toolbar>
      <Divider />

      <List>
        {hrMenuItems.map((item) => renderMenuItem(item))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: {
            sm: desktopOpen
              ? `calc(100% - ${drawerWidth}px)`
              : `calc(100% - ${collapsedDrawerWidth}px)`,
          },
          ml: {
            sm: desktopOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px`,
          },
          bgcolor: HR_COLOR,
          transition: 'width 0.3s ease, margin 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDesktopDrawerToggle}
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {getPageTitle()}
          </Typography>
          <HeaderChatButton />
          <NotificationsBell />
          <ThemeToggle />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        onMouseEnter={() => {
          if (!desktopOpen) setDesktopOpen(true);
        }}
        sx={{
          width: { sm: desktopOpen ? drawerWidth : collapsedDrawerWidth },
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopOpen ? drawerWidth : collapsedDrawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
          open={desktopOpen}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        onClick={() => {
          if (desktopOpen) setDesktopOpen(false);
        }}
        sx={{
          flexGrow: 1,
          py: 3,
          px: 2,
          width: {
            sm: desktopOpen
              ? `calc(100% - ${drawerWidth}px)`
              : `calc(100% - ${collapsedDrawerWidth}px)`,
          },
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default HRLayout;
